import { useEffect, useState } from "react";
import {
  EuiButton,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiCallOut,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../can";

const i18n = "anketa.opsanio.hardware.ticket";

export type HardwareTicketType = "replacement" | "inspection" | "return";
const TYPES: HardwareTicketType[] = ["replacement", "inspection", "return"];
const PRIORITIES = ["1 very low", "2 low", "3 normal", "4 high", "5 very high"];

/**
 * May the current user open hardware cases? Same grant as the server route:
 * opsanio/hardware · create AND opsanio/otobo · create (internal).
 */
export const useCanOpenHardwareTicket = (): boolean => {
  const can = useOpsanioCan();
  return (
    can(AnketaResourcesEnum.opsanioHardware, "create") &&
    can(AnketaResourcesEnum.opsanioOtobo, "create", { isInternal: true })
  );
};

export interface HardwareTicketTarget {
  deviceId: number;
  deviceName: string;
  /** Preselected component (e.g. picked from the flyout's component table). */
  component?: { id: number; name: string } | null;
}

/** A device component (NetBox inventory item) as the picker lists it. */
interface HardwareComponent {
  id: number;
  name: string;
  partId: string;
  serial: string;
  manufacturer: string;
  status: string | null;
  statusLabel: string | null;
}

const componentLabel = (c: HardwareComponent): string =>
  [
    c.name || `#${c.id}`,
    c.manufacturer,
    c.partId,
    c.serial ? `S/N ${c.serial}` : "",
    c.status === "failed" ? `(${c.statusLabel ?? "failed"})` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Open a hardware case (Replacement / Inspection / Return): an OTOBO ticket in
 * the central hardware queue, after which NetBox marks the device - or the
 * component - as failed.
 */
export const HardwareTicketModal = ({
  target,
  onClose,
  onDone,
}: {
  target: HardwareTicketTarget;
  onClose: () => void;
  /** Called after the ticket exists (NetBox may have been updated). */
  onDone: () => void;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();
  const [type, setType] = useState<HardwareTicketType>("inspection");
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<string>("3 normal");
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Components of the device; the selection decides what is marked failed.
  const [components, setComponents] = useState<HardwareComponent[] | null>(null);
  const [componentsError, setComponentsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EuiComboBoxOptionOption<number>[]>(
    target.component
      ? [{ label: target.component.name, value: target.component.id }]
      : [],
  );

  useEffect(() => {
    if (typeof keycloak?.token !== "string") return;
    let cancelled = false;
    Get<any>({
      path: `v2/netbox/hardware/${target.deviceId}/components`,
      responseType: "json",
      token: keycloak.token,
    }).then((resp) => {
      if (cancelled) return;
      const list = (resp.data as any)?.data;
      if (!resp.success || !Array.isArray(list)) {
        // An empty answer collapses to data=null: no components, not an error.
        if (resp.success) setComponents([]);
        else setComponentsError((resp.error as any)?.service?.message ?? resp.error?.message ?? "");
        return;
      }
      setComponents(list);
      // Show the preselected component with its full label once known.
      setSelected((current) =>
        current.map((option) => {
          const match = list.find((c: HardwareComponent) => c.id === option.value);
          return match ? { label: componentLabel(match), value: match.id } : option;
        }),
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.deviceId, keycloak?.token]);

  const typeLabel = (t: HardwareTicketType): string =>
    ({
      replacement: translate(`${i18n}.type.replacement`, { defaultMessage: "Replacement" }),
      inspection: translate(`${i18n}.type.inspection`, { defaultMessage: "Inspection" }),
      return: translate(`${i18n}.type.return`, { defaultMessage: "Return" }),
    })[t];

  const componentIds = selected
    .map((option) => option.value)
    .filter((id): id is number => typeof id === "number");

  const submit = async () => {
    if (description.trim() === "" || typeof keycloak?.token !== "string") return;
    setSending(true);
    setError(null);
    const resp = await Post<any>({
      path: `v2/netbox/hardware/${target.deviceId}/ticket`,
      value: {
        type,
        description: description.trim(),
        priority,
        ...(componentIds.length > 0 ? { componentIds } : {}),
      },
      responseType: "json",
      token: keycloak.token,
    });
    setSending(false);
    const data = (resp.data as any)?.data;
    if (!resp.success || !data?.ticket) {
      setError(
        (resp.error as any)?.service?.message ??
          resp.error?.message ??
          translate(`${i18n}.failed`, { defaultMessage: "The hardware case could not be opened." }),
      );
      return;
    }
    addToast({
      color: "success",
      title: translate(`${i18n}.success`, { defaultMessage: "Hardware case opened" }),
      text: `#${data.ticket.TicketNumber || data.ticket.TicketID} · ${data.queue}`,
    });
    // The ticket exists; a NetBox update that failed afterwards is reported, not hidden.
    const warnings: string[] = data.netbox?.warnings ?? [];
    if (warnings.length > 0) {
      addToast({
        color: "warning",
        title: translate(`${i18n}.netboxWarning`, {
          defaultMessage: "Ticket created, but NetBox was not fully updated",
        }),
        text: warnings.join(" · "),
      });
    }
    onDone();
  };

  return (
    <EuiModal onClose={onClose} style={{ width: 640 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          {translate(`${i18n}.title`, { defaultMessage: "Hardware case" })}
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText size="s">
          <strong>{target.deviceName}</strong>
        </EuiText>
        <EuiSpacer size="m" />
        <EuiFormRow
          fullWidth
          label={translate(`${i18n}.field.components`, {
            defaultMessage: "Components",
          })}
          helpText={translate(`${i18n}.field.componentsHelp`, {
            defaultMessage: "Pick the affected components - leave empty for the whole device.",
          })}
          isInvalid={componentsError !== null}
          error={
            componentsError !== null
              ? componentsError ||
                translate(`${i18n}.field.componentsFailed`, {
                  defaultMessage: "The components could not be loaded.",
                })
              : undefined
          }
        >
          <EuiComboBox<number>
            fullWidth
            isLoading={components === null && componentsError === null}
            isDisabled={sending}
            placeholder={translate(`${i18n}.field.componentsPlaceholder`, {
              defaultMessage: "Whole device",
            })}
            options={(components ?? []).map((c) => ({
              label: componentLabel(c),
              value: c.id,
            }))}
            selectedOptions={selected}
            onChange={(options) => setSelected(options)}
          />
        </EuiFormRow>
        <EuiFormRow label={translate(`${i18n}.field.type`, { defaultMessage: "Type" })} fullWidth>
          <EuiButtonGroup
            legend={translate(`${i18n}.field.type`, { defaultMessage: "Type" })}
            options={TYPES.map((t) => ({ id: t, label: typeLabel(t) }))}
            idSelected={type}
            onChange={(id: string) => setType(id as HardwareTicketType)}
            isDisabled={sending}
            buttonSize="compressed"
          />
        </EuiFormRow>
        <EuiFormRow
          label={translate(`${i18n}.field.description`, {
            defaultMessage: "Description / fault",
          })}
          fullWidth
        >
          <EuiTextArea
            fullWidth
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={sending}
            maxLength={5000}
          />
        </EuiFormRow>
        <EuiFormRow label={translate(`${i18n}.field.priority`, { defaultMessage: "Priority" })}>
          <EuiSelect
            compressed
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={sending}
            options={PRIORITIES.map((p) => ({ value: p, text: p }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiCallOut
          size="s"
          color="warning"
          iconType="alert"
          title={translate(`${i18n}.netboxNote`, {
            values: {
              what:
                componentIds.length === 0
                  ? translate(`${i18n}.what.device`, { defaultMessage: "device" })
                  : componentIds.length === 1
                    ? translate(`${i18n}.what.component`, { defaultMessage: "component" })
                    : translate(`${i18n}.what.components`, {
                        values: { count: componentIds.length },
                        defaultMessage: "{{count}} components",
                      }),
            },
            defaultMessage:
              "After the ticket is created, the {{what}} is set to \"failed\" in NetBox (with a journal entry naming the ticket).",
          })}
        />
        {error && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
          </>
        )}
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose} disabled={sending}>
          {translate("anketa.common.cancel", { defaultMessage: "Cancel" })}
        </EuiButtonEmpty>
        <EuiButton
          fill
          color="warning"
          iconType="wrench"
          onClick={submit}
          isLoading={sending}
          isDisabled={description.trim() === ""}
        >
          {translate(`${i18n}.submit`, { defaultMessage: "Open case" })}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
